import { useEffect, useState, type ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { usePubliekDossier, usePortaalBranding, portaalApi, PortaalFout } from '../hooks/useKlantportaal.js';
import { PLATFORM_NAAM } from '../../../core/shared/kordaroBrand.js';
import { KIcon, type KIconNaam } from '../../../core/shared/components/KIcon.js';
import { KlantDossierScherm } from '../components/KlantDossierScherm.js';

/** /portaal/:token — de link is de toegang. Laden/fout, PWA-tags en titel; het scherm zelf staat in KlantDossierScherm. */

// ─── Statusschermen (laden / ongeldig / fout) ──────────────

function Statusscherm({ icoon, titel, children, laden }: { icoon?: KIconNaam; titel: string; children?: ReactNode; laden?: boolean }) {
  return (
    <div className="k-ui min-h-[100dvh] flex items-center justify-center px-4">
      <div className="text-center max-w-md" role={laden ? 'status' : undefined} aria-busy={laden || undefined}>
        {icoon && <span className="inline-flex text-[var(--k-muted)] mb-4"><KIcon naam={icoon} size={40} /></span>}
        {laden && <span className="block w-10 h-10 mx-auto mb-4 rounded-full border-2 border-[var(--k-border)] border-t-[var(--k-brand)] animate-spin motion-reduce:animate-none" aria-hidden="true" />}
        <h1 className="k-section-title mb-2">{titel}</h1>
        {children}
      </div>
    </div>
  );
}

/**
 * Rustige melding: de link blijft werken tijdens de overgang. Geen koppeling via de token; een account komt uit een uitnodiging.
 * Staat uit tot winkels echt uitnodigen (keuze Sanju 5/11): anders vraagt elke klant op een oude link naar een account dat hij nog niet kan maken.
 */
const TOON_MAAK_ACCOUNT = false;
const MELDING_WEG = 'kordaro-maak-account-weg';
function MaakAccountMelding() {
  const [weg, setWeg] = useState(() => { try { return localStorage.getItem(MELDING_WEG) === '1'; } catch { return false; } });
  if (weg) return null;
  return (
    <div className="k-ui border-b border-[var(--k-border)] bg-[var(--k-surface)]">
      <div className="max-w-5xl mx-auto px-4 md:px-6 py-1 flex items-center justify-between gap-3">
        <p className="k-meta">
          <a className="k-link" href="/mijn">Maak je account</a> en volg je dossier met je eigen wachtwoord. Je winkel stuurt je daarvoor een uitnodiging.
        </p>
        <button type="button" className="k-button k-button--icon shrink-0" aria-label="Melding sluiten"
          onClick={() => { setWeg(true); try { localStorage.setItem(MELDING_WEG, '1'); } catch { /* enkel gemak */ } }}>
          <KIcon naam="sluit" />
        </button>
      </div>
    </div>
  );
}

export function KlantportaalPage() {
  const { token = '' } = useParams<{ token: string }>();
  const api = portaalApi(token);
  const { data: dossier, isLoading, error, refetch, isFetching } = usePubliekDossier(api);
  const { data: branding } = usePortaalBranding(api);

  // PWA: "Zet op beginscherm" moet een app installeren die dít dossier opent, niet het loginscherm.
  // We buigen de bestaande manifest-/icoonlinks om naar dit portaal en zetten ze terug bij het verlaten.
  const appNaam = branding?.bedrijfsNaam || PLATFORM_NAAM;
  useEffect(() => {
    if (!token || !dossier) return;
    const head = document.head;
    const herstel: Array<() => void> = [];
    const zet = (selector: string, maak: () => HTMLElement, attr: string, waarde: string) => {
      let el = head.querySelector<HTMLElement>(selector);
      if (el) {
        const vast = el;
        const vorige = vast.getAttribute(attr);
        herstel.push(() => { if (vorige === null) vast.removeAttribute(attr); else vast.setAttribute(attr, vorige); });
      } else {
        el = maak();
        head.appendChild(el);
        const nieuw = el;
        herstel.push(() => nieuw.remove());
      }
      el.setAttribute(attr, waarde);
    };
    const link = (rel: string) => () => { const l = document.createElement('link'); l.rel = rel; return l; };
    const meta = (name: string) => () => { const m = document.createElement('meta'); m.name = name; return m; };
    const t = encodeURIComponent(token);
    zet('link[rel="manifest"]', link('manifest'), 'href', `/api/klantportaal/${t}/manifest.webmanifest`);
    zet('link[rel="apple-touch-icon"]', link('apple-touch-icon'), 'href', `/api/klantportaal/${t}/apple-touch-icon.png`);
    zet('meta[name="apple-mobile-web-app-title"]', meta('apple-mobile-web-app-title'), 'content', appNaam.slice(0, 15));
    return () => { herstel.reverse().forEach((f) => f()); };
  }, [token, dossier, appNaam]);

  // Paginatitel: winkel + "Mijn keuken" (zichtbaar in tabblad en bij installeren)
  useEffect(() => {
    if (!dossier) return;
    const vorige = document.title;
    document.title = `Mijn keuken — ${appNaam}`;
    return () => { document.title = vorige; };
  }, [dossier, appNaam]);

  if (isLoading) {
    return <Statusscherm laden titel="Uw dossier wordt geladen…" />;
  }

  if (error || !dossier) {
    const status = error instanceof PortaalFout ? error.status : null;
    if (status === 404 || (status !== null && status >= 400 && status < 500 && status !== 429)) {
      return (
        <Statusscherm icoon="slot" titel="Deze link werkt niet meer">
          <p className="mb-2">De link naar uw dossier is verlopen of vervangen door een nieuwe.</p>
          <p className="k-meta">Bel of mail de winkel waar u uw keuken kocht. Zij sturen u graag een nieuwe link.</p>
        </Statusscherm>
      );
    }
    return (
      <Statusscherm icoon="offline" titel="Uw dossier kan nu niet geladen worden">
        <p className="mb-4">{status === 429 ? 'Er werd net te vaak geladen. Wacht een minuut en probeer opnieuw.' : 'Dit ligt niet aan u. Controleer uw verbinding of probeer het over enkele minuten opnieuw.'}</p>
        <button type="button" className="k-button" onClick={() => refetch()} disabled={isFetching} aria-busy={isFetching || undefined}>
          {isFetching ? 'Bezig…' : 'Opnieuw proberen'}
        </button>
      </Statusscherm>
    );
  }

  return (
    <>
      {TOON_MAAK_ACCOUNT && <MaakAccountMelding />}
      <KlantDossierScherm dossier={dossier} branding={branding} api={api} />
    </>
  );
}
